import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import style from './GeneticResults.module.css';

import axiosApi from '../../api/daonAxios';
import noDataImg from '../../images/common/i_3d_no_data_doc.svg';


const LEGEND = [
    { key: 'good',     color: '#1065D9', label: '양호' },
    { key: 'interest', color: '#FF8D29', label: '관심' },
    { key: 'warn',     color: '#E55151', label: '주의' },
];

const BAR_COLORS = { warn: '#E55151', interest: '#FF8D29', good: '#1065D9' };

const CANCER_MP_IDS = [
    'mp612',// 위암
    'mp608',// 대장암
    'mp614',// 췌장암
    'mp603', // 간암
    'mp615',// 폐암
    'mp613',// 전립선암
];

const RiskBar = ({ risk, level, animate }) => {
    // risk = 배수 (예: 1.74), avg는 항상 1.0배 기준
    const MAX = 3.0;
    const riskPercent = animate ? Math.min((risk / MAX) * 100, 100) : 0;
    // const avgPercent = (1.0 / MAX) * 100;  // 한국인 평균은 항상 1배

    return (
        <div className={style.barGroup}>
            <div className={style.barBg}>
                <div className={style.barFill} style={{
                    width: `${riskPercent}%`,
                    backgroundColor: BAR_COLORS[level],
                    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                }} />
            </div>
            {/* <div className={style.barBg} data-bar="avg">
                <div className={style.barFill} data-bar="avg" style={{
                    width: `${avgPercent}%`,
                    transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
                }} />
            </div> */}
        </div>
    );
};

const GeneticResults = () => {
    // const [dateIdx, setDateIdx] = useState(MOCK_DATES.length - 1);
    const [subTab, setSubTab] = useState('cancer');
    const [animate, setAnimate] = useState(false);
    const subTabBarRef = useRef(null);
    const [subIndicatorStyle, setSubIndicatorStyle] = useState({});

    const [macrogenMas, setMacrogenMas] = useState([]);

    const [patntGenetRgstList, setPatntGenetRgstList] = useState([]);

    const [genomeData, setGenomeData] = useState([]);
    const [loading, setLoading] = useState(true);

    // 유전체별 외부 API 호출 이력
    const apiCalledRef = useRef(new Set());

    const [dateIdx, setDateIdx] = useState(0);

    const dates = useMemo(() => {

        return genomeData
            .map(item => item.mcrSpcmnDe)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));

    }, [genomeData]);

    const currentDate = dates[dateIdx];

    useEffect(() => {
        setAnimate(false);
        const timer = setTimeout(() => setAnimate(true), 50);
        return () => clearTimeout(timer);
    }, [dateIdx, subTab]);


    //탭 인디케이트 
    const updateSubIndicator = (key) => {
        const bar = subTabBarRef.current;
        if (!bar) return;
        const btn = bar.querySelector(`[data-subtab="${key}"]`);
        if (!btn) return;
        const barRect = bar.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        setSubIndicatorStyle({
            left: btnRect.left - barRect.left,
            width: btnRect.width,
        });
    };
    useEffect(() => {
        updateSubIndicator(subTab);
    }, [subTab, loading, genomeData.length]);

    useEffect(() => {
        const bar = subTabBarRef.current;
        if (!bar) return;
        const ro = new ResizeObserver(() => updateSubIndicator(subTab));
        ro.observe(bar);
        return () => ro.disconnect();
    }, [subTab, loading, genomeData.length]);


    /////////////////////////


    const makeGenomeData = (patntGenetRgstList, macrogenMas) => {

        const masterMap = new Map();

        macrogenMas.forEach(item => {
            if (!masterMap.has(item.mpId)) {
                masterMap.set(item.mpId, item);
            }
        });

        return patntGenetRgstList.map(rgst => {

            let orgData = null;

            if (rgst.mcrOrglData) {
                try {
                    orgData = typeof rgst.mcrOrglData === 'string'
                        ? JSON.parse(rgst.mcrOrglData)
                        : rgst.mcrOrglData;
                } catch (e) {
                    console.error('mcrOrglData 파싱 실패', e);
                }
            }

            const mpIds = orgData?.mpIds || {};

            const cancer = [];

            const general = {brain: [], heart: [], wholebody: [],};

            Object.entries(mpIds).forEach(([mpId, result]) => {

                const master = masterMap.get(mpId);

                if (!master) return;

                const item = {
                    mpId,
                    majorGroup: master.majorGroup,
                    dissCtgry: master.dissCtgry,
                    ptKor: master.ptKor,
                    ptEng: master.ptEng,

                    grade: result.grade,
                    riskRatio: result.riskRatio,
                    riskScorePerc: result.riskScorePerc,
                };

                // 암질환
                if (master.majorGroup === '암') {

                    if (CANCER_MP_IDS.includes(mpId)) {
                        cancer.push(item);
                    }

                    return;
                }

                // 일반질환
                if (master.majorGroup === '일반질환') {

                    const dissCtgryList = (master.dissCtgry || '')
                        .split(',')
                        .map(v => v.trim());

                    if (dissCtgryList.includes('뇌질환')) {
                        general.brain.push(item);
                    }

                    if (dissCtgryList.includes('심장')) {
                        general.heart.push(item);
                    }

                    if (dissCtgryList.includes('전신')) {
                        general.wholebody.push(item);
                    }
                }
            });

            // 각 그룹 riskRatio 내림차순
            cancer.sort(
                (a, b) =>
                    Number(b.riskRatio ?? 0) -
                    Number(a.riskRatio ?? 0)
            );

            general.brain.sort(
                (a, b) =>
                    Number(b.riskRatio ?? 0) -
                    Number(a.riskRatio ?? 0)
            );

            general.heart.sort(
                (a, b) =>
                    Number(b.riskRatio ?? 0) -
                    Number(a.riskRatio ?? 0)
            );

            general.wholebody.sort(
                (a, b) =>
                    Number(b.riskRatio ?? 0) -
                    Number(a.riskRatio ?? 0)
            );

            return {...rgst, orgData, cancer, general,};
        });
    };


    const currentGenomeData = useMemo(() => {

        if (!currentDate) return null;

        return genomeData.find(
            item => item.mcrSpcmnDe === currentDate
        );

    }, [genomeData, currentDate]);

    const hasGenomeResult = useMemo(() => {
        return !!currentGenomeData?.orgData?.mpIds;
    }, [currentGenomeData]);

    useEffect(() => {

        if (dates.length > 0) {
            setDateIdx(dates.length - 1);
        }

    }, [dates.length]);

    const data = useMemo(() => {

        if (!currentGenomeData) return [];

        // 암질환
        if (subTab === 'cancer') {

            return [
                {
                    title: '암질환',
                    key: 'cancer',
                    items: currentGenomeData.cancer,
                }
            ];
        }

        // 일반질환
        return [
            {
                title: '뇌질환',
                key: 'brain',
                items: currentGenomeData.general.brain,
            },
            {
                title: '심장',
                key: 'heart',
                items: currentGenomeData.general.heart,
            },
            {
                title: '전신',
                key: 'wholebody',
                items: currentGenomeData.general.wholebody,
            },
        ].filter(group => group.items.length > 0);

    }, [currentGenomeData, subTab]);


    const getMacrogenMaster = useCallback(async () => {
        try {

            const loginUser = JSON.parse(sessionStorage.getItem('loginUser'));

            const jToken = localStorage.getItem("jToken");

            const patntRgstId = loginUser.usrHsptlMap.find((a) => a.cnncHsptlCd === "012").patntRgstId;

            const requestOptions = {
                params : {
                    patntRgstId: patntRgstId,
                },
                headers: {
                    "Authorization": `Bearer ${jToken}`,
                },
            }

            
            const response = await axiosApi.get('/lab/macrogen/data', requestOptions);
            
            setMacrogenMas(response.data.result.macrogenMas || []);
            setPatntGenetRgstList(response.data.result.patntGenetRgstList || []);
            
        } catch (error) {
            console.error('마크로젠 마스터 조회 실패', error);
            setMacrogenMas([]);
            setPatntGenetRgstList([]);
        } finally {
            setLoading(false);
         }
    },[]);

    useEffect(()=> {
        getMacrogenMaster();
    },[getMacrogenMaster])

    useEffect(() => {

        if (!patntGenetRgstList.length || !macrogenMas.length) {
            setGenomeData([]);
            return;
        }

        const result = makeGenomeData(patntGenetRgstList, macrogenMas);

        setGenomeData(result);

    }, [patntGenetRgstList, macrogenMas]);

    useEffect(() => {
        if (!currentGenomeData || hasGenomeResult) return;

        const genomeId = currentGenomeData.id;
        const kitId = currentGenomeData.kitId;

        if (genomeId == null || !kitId) return;

        // 이미 API 호출한 유전체는 다시 호출하지 않음
        if (apiCalledRef.current.has(genomeId)) return;

        apiCalledRef.current.add(genomeId);

        const callMacrogenApi = async () => {
            try {
                const jToken = localStorage.getItem('jToken');

                const requestOptions = {
                    headers: {
                        Authorization: `Bearer ${jToken}`,
                    },
                };

                const requestData = {
                    id: genomeId,
                    kitId,
                };

                // 1. 마크로젠 API 호출
                await axiosApi.post(
                    '/lab/macrogen/api',
                    requestData,
                    requestOptions
                );

                // 2. API 호출 후 DB 재조회
                await getMacrogenMaster();

            } catch (error) {
                console.error('마크로젠 분석 결과 조회 실패', error);
            }
        };

        callMacrogenApi();

    }, [currentGenomeData, hasGenomeResult, getMacrogenMaster]);

    const getRiskLevel = (grade) => {

        switch (grade) {

            case '집중관리':
                return 'warn';

            case '관심관리':
                return 'interest';

            case '일반관리':
                return 'good';

            default:
                return 'good';
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';

        const [year, month, day] = date.split('-');

        return `${year.slice(2)}.${month}.${day}`;
    };

    const downloadMacrogenPdf = async () => {
        try {
            const kitId = currentGenomeData?.kitId;

            if (!kitId) {
                alert("KIT ID가 없습니다.");
                return;
            }

            const jToken = localStorage.getItem("jToken");

            const response = await axiosApi.get(
                "/lab/macrogen/pdf",
                {
                    params: {
                        kitId: kitId,
                    },
                    headers: {
                        Authorization: `Bearer ${jToken}`,
                    },
                    responseType: "blob",
                }
            );

            const blob = response.data;
            const fileName = `유전체_${kitId}.pdf`;

            // 일반 브라우저
            if (!window.AndroidBridge && !window.webkit?.messageHandlers?.downloadFile) {
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                return;
            }

            // 웹앱
            const base64 = await blobToBase64(blob);

            // Android
            if (window.AndroidBridge?.downloadFile) {
                window.AndroidBridge.downloadFile(
                    fileName,
                    base64
                );
            }
            // iOS
            else if (window.webkit?.messageHandlers?.downloadFile) {
                window.webkit.messageHandlers.downloadFile.postMessage({
                    fileName,
                    base64,
                });
            }

        } catch (error) {
            console.error("마크로젠 PDF 다운로드 실패", error);
            alert("PDF 다운로드에 실패했습니다.");
        }
    };

    const blobToBase64 = (blob) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => {
                const result = reader.result;

                // data:application/pdf;base64, 부분 제거
                const base64 = result.split(",")[1];

                resolve(base64);
            };

            reader.onerror = reject;

            reader.readAsDataURL(blob);
        });
    };
     
    return (
        <div className={style.wrap}>

            {/* 유전자 검사 기록 자체가 없음 */}
            {loading ? null : genomeData.length === 0 || !hasGenomeResult ? (

                <div className={style['no-data-box']}>
                    <div className={style['img-box']}>
                        <img
                            src={noDataImg}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>

                    <p>유전체 데이터를 분석하고 있어요</p>
                    <span>
                        채혈 후 결과 확인까지 약 2주가 소요됩니다.
                        <br />
                        분석이 완료되면 결과가 자동으로 업데이트됩니다.
                    </span>
                </div>

            ) : (
            <>
                <div className={style.dateNav}>
                    <button className={style.dateArrow} data-arrow="prev" onClick={() => setDateIdx(i => i - 1)} disabled={dateIdx === 0}></button>
                    <span className={style.dateText}>검진일 {formatDate(currentDate)}</span>
                    <button className={style.dateArrow} data-arrow="next" onClick={() => setDateIdx(i => i + 1)} disabled={dateIdx === dates.length - 1}></button>
                </div>

                <div className={style.subTabBar} ref={subTabBarRef}>
                    <button data-subtab="cancer" className={`${style.subTabBtn} ${subTab === 'cancer' ? style.active : ''}`} onClick={() => setSubTab('cancer')}>암 질환</button>
                    <button data-subtab="general" className={`${style.subTabBtn} ${subTab === 'general' ? style.active : ''}`} onClick={() => setSubTab('general')}>일반 질환</button>
                    <span className={style.subTabSlider} style={subIndicatorStyle} />
                </div>

                <div className={style.topRow}>
                    <h3 className={style.sectionTitle}>주요 검사 결과</h3>
                    <button className={style.pdfBtn} onClick={downloadMacrogenPdf}>검진 전체 결과 확인 (PDF)</button>
                </div>
                
                <div className={style.legendRow}>
                    {LEGEND.map(l => (
                        <span key={l.key} className={style.legendItem}>
                            <span className={style.legendDot} style={{ backgroundColor: l.color }} />{l.label}
                        </span>
                    ))}
                    {/* <span className={style.legendItem}><span className={style.legendBarAvg} />한국인 평균</span> */}
                    <span className={style.legendItem} data-item="risk">유전적 위험 수치</span>
                </div>

                <div className={style.cardList}>
                    {data.length === 0 ? (
                        /* 기록은 있지만 현재 탭(암질환/일반질환)에 항목이 없음 */
                        <div className={style['no-data-box']} data-size="sm">
                            <div className={style['img-box']}>
                                <img src={noDataImg} alt="" aria-hidden="true" />
                            </div>
                            <p>
                                {subTab === 'cancer' ? '암 질환' : '일반 질환'} 결과가 없습니다.
                            </p>
                        </div>
                    ) : data.map((group, gi) => (
                        <div key={`${currentDate}-${subTab}-${gi}`} className={style.card}>
                            <h4 className={style.cardTitle}><span className={style.iconBox} data-icon={group.key}></span> {group.title}</h4>
                            <div className={style.itemList}>
                                {group.items.map((item, ii) => (
                                    <div key={ii} className={style.riskItem}>
                                        <strong className={style.riskName}>{item.ptKor}</strong>
                                        <RiskBar risk={item.riskRatio} level={getRiskLevel(item.grade)} animate={animate} />
                                        <span className={style.riskBadge}>{Number(item.riskRatio).toFixed(2)}배</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                </>
            )}
        </div>
    );
};

export default GeneticResults;